import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError } from '../../../../services/inventoryItemService';
import type { WineSearchResult, Wine } from '../../../../services/wineService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import wineListService from '../../../../services/wineListService';
import wineListItemService from '../../../../services/wineListItemService';
import type { InventoryItem, InventoryItemType, SizeUnit, WineList } from '../../../../types/venue';

const SIZE_UNITS: SizeUnit[] = ['ml', 'cl', 'l', 'g', 'kg', 'unit'];

const InventoryItemForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { itemId } = useParams<{ itemId?: string }>();
  const isEdit = Boolean(itemId);

  const backPath = isEdit
    ? `/admin/venues/${venueId}/inventory/${itemId}`
    : `/admin/venues/${venueId}/inventory`;

  // ── Edit mode: load existing item ─────────────────────────────────────────────
  const [initLoading, setInitLoading] = useState(isEdit);
  const [initError, setInitError] = useState<string | null>(null);
  const [loadedItem, setLoadedItem] = useState<InventoryItem | null>(null);

  // ── Create mode: item type toggle ─────────────────────────────────────────────
  const [itemType, setItemType] = useState<InventoryItemType>('GENERIC');

  // ── Create mode: GENERIC — display name ──────────────────────────────────────
  const [displayName, setDisplayName] = useState('');

  // ── Create mode: WINE — wine search ──────────────────────────────────────────
  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  const [createWineOpen, setCreateWineOpen] = useState(false);
  const [createWineQuery, setCreateWineQuery] = useState('');

  // ── Size (create mode) ────────────────────────────────────────────────────────
  const [sizeValue, setSizeValue] = useState('');
  const [sizeUnit, setSizeUnit] = useState<SizeUnit>('ml');

  // ── Config (create + edit) ────────────────────────────────────────────────────
  const [parLevel, setParLevel] = useState('');
  const [reorderPoint, setReorderPoint] = useState('');

  // ── Submission ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Validation ────────────────────────────────────────────────────────────────
  const [touched, setTouched] = useState(false);

  const genericNameError = touched && !isEdit && itemType === 'GENERIC' && !displayName.trim();
  const wineError = touched && !isEdit && itemType === 'WINE' && !selectedWine;

  const parLevelNum = parLevel !== '' ? Number(parLevel) : undefined;
  const reorderPointNum = reorderPoint !== '' ? Number(reorderPoint) : undefined;
  const sizeValueNum = sizeValue !== '' ? Number(sizeValue) : undefined;
  const reorderExceedsPar =
    parLevelNum !== undefined && reorderPointNum !== undefined && reorderPointNum > parLevelNum;

  // ── Post-create: add to wine list dialog (wine-linked items only) ─────────────
  const [postCreate, setPostCreate] = useState<{ itemId: string; wineId: string; sizeValue?: number } | null>(null);
  const [wineLists, setWineLists] = useState<WineList[]>([]);
  const [wineListsLoading, setWineListsLoading] = useState(false);
  const [selectedWineListId, setSelectedWineListId] = useState('');
  const [wineListVisibility, setWineListVisibility] = useState<'HIDDEN' | 'LIVE'>('LIVE');
  const [addingToList, setAddingToList] = useState(false);
  const [addToListError, setAddToListError] = useState('');

  const navigateToCreated = (createdItemId: string) =>
    navigate(`/admin/venues/${venueId}/inventory/${createdItemId}`);

  const handleSkipWineList = () => {
    if (postCreate) navigateToCreated(postCreate.itemId);
  };

  const handleAddToWineList = async () => {
    if (!postCreate || !selectedWineListId) return;
    setAddingToList(true);
    setAddToListError('');
    try {
      await wineListItemService.createItem(venueId, selectedWineListId, {
        wineId: postCreate.wineId,
        inventoryItemId: postCreate.itemId,
        visibility: wineListVisibility,
        source: 'MANUAL',
        active: true,
        ...(postCreate.sizeValue != null && { bottleSize: postCreate.sizeValue }),
        ...(defaultCurrency && { currency: defaultCurrency }),
      } as any);
      navigateToCreated(postCreate.itemId);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message ?? '';
      if (status === 409 || msg.toLowerCase().includes('exists') || msg.toLowerCase().includes('duplicate')) {
        setAddToListError('This wine is already on the selected wine list.');
      } else {
        setAddToListError('Failed to add to wine list. Please try again.');
      }
    } finally {
      setAddingToList(false);
    }
  };

  // ── Create new wine: convert Wine → WineSearchResult and select ──────────────
  const handleWineCreated = (wine: Wine) => {
    setSelectedWine({
      _id: wine._id,
      name: wine.name,
      displayVintage: wine.displayVintage,
      vintageType: wine.vintageType,
      wineDisplay: {
        producerName: wine.wineName.producer?.name,
        regionName: wine.wineName.region?.name,
        color: wine.wineName.color,
      },
    });
    setCreateWineOpen(false);
  };

  // ── Load item for edit mode ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !itemId || !venueId) return;
    setInitLoading(true);
    inventoryItemService.getItem(venueId, itemId)
      .then(item => {
        setLoadedItem(item);
        if (item.parLevel != null) setParLevel(String(item.parLevel));
        if (item.reorderPoint != null) setReorderPoint(String(item.reorderPoint));
        setInitError(null);
      })
      .catch(() => setInitError('Failed to load inventory item.'))
      .finally(() => setInitLoading(false));
  }, [isEdit, itemId, venueId]);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setTouched(true);
    if (!isEdit) {
      if (itemType === 'GENERIC' && !displayName.trim()) return;
      if (itemType === 'WINE' && !selectedWine) return;
    }
    if (reorderExceedsPar) return;

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && itemId) {
        await inventoryItemService.updateItem(venueId, itemId, {
          ...(parLevel !== '' ? { parLevel: Number(parLevel) } : {}),
          ...(reorderPoint !== '' ? { reorderPoint: Number(reorderPoint) } : {}),
        });
        navigate(backPath);
      } else {
        const payload =
          itemType === 'GENERIC'
            ? {
                type: 'GENERIC' as const,
                displayName: displayName.trim(),
                ...(sizeValueNum !== undefined && { sizeValue: sizeValueNum, sizeUnit }),
                ...(parLevelNum !== undefined && { parLevel: parLevelNum }),
                ...(reorderPointNum !== undefined && { reorderPoint: reorderPointNum }),
              }
            : {
                type: 'WINE' as const,
                wineId: selectedWine!._id,
                ...(sizeValueNum !== undefined && { sizeValue: sizeValueNum, sizeUnit }),
                ...(parLevelNum !== undefined && { parLevel: parLevelNum }),
                ...(reorderPointNum !== undefined && { reorderPoint: reorderPointNum }),
              };

        const created = await inventoryItemService.createItem(venueId, payload);

        // Only offer wine list prompt when the created item is linked to a wine
        if (created.wineId) {
          setWineListsLoading(true);
          try {
            const result = await wineListService.getWineLists(venueId, 1, 100);
            const activeLists = result.data.filter(l => l.status !== 'ARCHIVED');
            setWineLists(activeLists);
            if (activeLists.length > 0) setSelectedWineListId(activeLists[0]._id);
          } catch {
            setWineLists([]);
          } finally {
            setWineListsLoading(false);
          }
          setPostCreate({ itemId: created._id, wineId: created.wineId, sizeValue: created.sizeValue });
        } else {
          navigateToCreated(created._id);
        }
      }
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state (edit init) ─────────────────────────────────────────────────
  if (initLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: theme.palette.text.secondary }}>
          Back
        </Button>
        <Alert severity="error">{initError}</Alert>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 680 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            {isEdit ? 'Item Detail' : 'Inventory'}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>
            {isEdit ? 'Edit Item Configuration' : 'Add Inventory Item'}
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {/* ── Create mode ────────────────────────────────────────────────────── */}
          {!isEdit && (
            <>
              {/* Item type toggle */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Item Type</Typography>
                <ToggleButtonGroup
                  value={itemType}
                  exclusive
                  onChange={(_, val) => { if (val) { setItemType(val); setDisplayName(''); setSelectedWine(null); } }}
                  size="small"
                >
                  <ToggleButton value="GENERIC" sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                    Generic
                  </ToggleButton>
                  <ToggleButton value="WINE" sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                    Wine
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* GENERIC: free-text name */}
              {itemType === 'GENERIC' && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Name</Typography>
                  <TextField
                    fullWidth
                    label="Display Name"
                    required
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    error={genericNameError}
                    helperText={genericNameError ? 'Enter a name for this item' : 'e.g. "Still Water 500 ml", "House Red"'}
                    inputProps={{ maxLength: 200 }}
                  />
                </Box>
              )}

              {/* WINE: wine search autocomplete */}
              {itemType === 'WINE' && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Wine</Typography>
                  <WineSearchBox
                    value={selectedWine}
                    onChange={wine => setSelectedWine(wine)}
                    onCreateNew={query => { setCreateWineQuery(query); setCreateWineOpen(true); }}
                    error={wineError}
                    helperText={wineError ? 'Select a wine' : undefined}
                  />
                </Box>
              )}

              {/* Size */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Size <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    size="small"
                    label="Size"
                    type="number"
                    value={sizeValue}
                    onChange={e => setSizeValue(e.target.value)}
                    inputProps={{ min: 1, step: 1 }}
                    helperText="e.g. 750"
                    sx={{ flex: 1, minWidth: 120 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 90 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={sizeUnit}
                      label="Unit"
                      onChange={e => setSizeUnit(e.target.value as SizeUnit)}
                    >
                      {SIZE_UNITS.map(u => (
                        <MenuItem key={u} value={u}>{u}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </>
          )}

          {/* ── Edit mode: read-only identity ──────────────────────────────────── */}
          {isEdit && loadedItem && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                    Item
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.25 }}>
                    {loadedItem.displayName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                      Type
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      {loadedItem.type === 'WINE' ? 'Wine' : 'Generic'}
                    </Typography>
                  </Box>
                  {loadedItem.sizeValue != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                        Size
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {loadedItem.sizeValue} {loadedItem.sizeUnit ?? 'ml'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Alert severity="info" icon={false}>
                Name, type, and size cannot be changed after creation.
                To change them, deactivate this item and create a new one.
              </Alert>
            </>
          )}

          <Divider />

          {/* Configuration */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Configuration
              {!isEdit && (
                <Typography component="span" variant="caption" color="text.secondary"> (optional)</Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Par Level"
                type="number"
                value={parLevel}
                onChange={e => setParLevel(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Target stock level"
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                size="small"
                label="Reorder Point"
                type="number"
                value={reorderPoint}
                onChange={e => setReorderPoint(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={reorderExceedsPar}
                helperText={reorderExceedsPar ? 'Must be ≤ par level' : 'Alert threshold'}
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>
          </Box>

          {/* Opening stock note (create only) */}
          {!isEdit && (
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.07),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
              }}
            >
              <Typography variant="body2" color="info.dark">
                The item starts with a <strong>quantity of 0</strong>. Post a Manual Adjustment
                to set opening stock.
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitting
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save Changes' : 'Create Item'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <CreateWineModal
        open={createWineOpen}
        initialQuery={createWineQuery}
        onCreated={handleWineCreated}
        onClose={() => setCreateWineOpen(false)}
      />

      {/* Post-create: add to wine list dialog (shown only for wine-linked items) */}
      <Dialog
        open={Boolean(postCreate)}
        onClose={handleSkipWineList}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Item created</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Would you like to also add this wine to a wine list?
          </Typography>

          {addToListError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddToListError('')}>
              {addToListError}
            </Alert>
          )}

          {wineListsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : wineLists.length === 0 ? (
            <Alert severity="info" icon={false}>
              No active wine lists found for this venue.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Wine list</InputLabel>
                <Select
                  value={selectedWineListId}
                  label="Wine list"
                  onChange={e => { setSelectedWineListId(e.target.value); setAddToListError(''); }}
                >
                  <MenuItem value="">— Select a wine list —</MenuItem>
                  {wineLists.map(l => (
                    <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={wineListVisibility}
                  label="Visibility"
                  onChange={e => setWineListVisibility(e.target.value as 'HIDDEN' | 'LIVE')}
                >
                  <MenuItem value="HIDDEN">Hidden</MenuItem>
                  <MenuItem value="LIVE">Live</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleSkipWineList} sx={{ textTransform: 'none' }} disabled={addingToList}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToWineList}
            disabled={addingToList || !selectedWineListId || wineListsLoading}
            startIcon={addingToList ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {addingToList ? 'Adding…' : 'Add to Wine List'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryItemForm;
